import { ToastContext } from "context/ToastContext";
import { Checkbox } from "primereact/checkbox";
import { Chips } from "primereact/chips";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { ProgressBar } from "primereact/progressbar";
import { useContext, useRef, useState } from "react";
import { useSplitRateFileMutation } from "store/queries/rateTools";
import styled from "styled-components";

import {
  buildSpecialtyOverrides,
  DEFAULT_SPECIALTIES,
  EMPTY_MODIFIER_SELECTION,
  fileModeLabel,
  IModifierSelection,
  ISplitStats,
  parseSplitStats,
} from "./utils";

// PrimeReact does not export the upload-handler event type, so we restate the only
// shape we read from the event here. (`uploadHandler?(e: FileUploadHandlerParam)`,
// where `FileUploadHandlerParam extends { files: File[] }`.)
interface IFileUploadHandlerEvent {
  files: File[];
}

/**
 * CLE-2323 — Rate File Splitter sub-tab on PayerRules → Tools.
 *
 * Upload one physician rate file (CSV or XLSX); the API auto-detects file shape
 * (Aetna-style balanced vs BCBS-style facility-as-exception), normalizes MOD/POS
 * values, and streams back a ZIP with `facility.csv` + `non-facility.csv`. The
 * analyst toggles which outputs to include and whether to carry through the
 * `Service Line` column.
 *
 * CLE-2514 — Specialty Overwrite: for chosen specialties (service lines), the analyst
 * declares exactly which modifiers each output should emit (Global / 26 / TC / 53 / Other /
 * no-modifier), independently for facility and non-facility. Each selected modifier becomes
 * its own output row tagged in a new `MOD` column, so a CPT can carry more than one price per
 * code+payer (the CLE-2482 modifier-pricing shape).
 *
 * Diagnostic stats (detected mode, row counts) come back in `x-split-*` response
 * headers and are surfaced in a results banner so the user can sanity-check what the
 * splitter did before opening the downloaded ZIP.
 */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 760px;
`;

const Toggles = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
  flex-wrap: wrap;

  label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
  }
`;

const OverridePanel = styled.div`
  border: 1px solid var(--surface-border);
  border-radius: 6px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ModifierGroups = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
`;

const ModifierColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 240px;

  label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
  }
`;

/** One side's "Modifiers to Include" checkbox group (Facility or Non-Facility). */
function ModifierGroup({
  title,
  value,
  onChange,
  disabled,
}: {
  title: string;
  value: IModifierSelection;
  onChange: (next: IModifierSelection) => void;
  disabled: boolean;
}) {
  const set = (patch: Partial<IModifierSelection>) =>
    onChange({ ...value, ...patch });
  return (
    <ModifierColumn>
      <strong>{title}</strong>
      <span style={{ color: "var(--gray-600)", fontSize: "0.85em" }}>
        Modifiers to Include:
      </span>
      <label>
        <Checkbox
          checked={value.global}
          disabled={disabled}
          onChange={(e) => set({ global: e.checked ?? false })}
        />
        Global
      </label>
      <label>
        <Checkbox
          checked={value.professional}
          disabled={disabled}
          onChange={(e) => set({ professional: e.checked ?? false })}
        />
        26 (Professional Component)
      </label>
      <label>
        <Checkbox
          checked={value.technical}
          disabled={disabled}
          onChange={(e) => set({ technical: e.checked ?? false })}
        />
        TC (Technical Component)
      </label>
      <label>
        <Checkbox
          checked={value.fiftyThree}
          disabled={disabled}
          onChange={(e) => set({ fiftyThree: e.checked ?? false })}
        />
        53
      </label>
      <label style={{ cursor: "default" }}>
        Other:
        <InputText
          value={value.other}
          disabled={disabled}
          onChange={(e) => set({ other: e.target.value })}
          placeholder="e.g. 50, XU"
          style={{ padding: "0.25rem 0.5rem", width: "10rem" }}
        />
      </label>
      <label>
        <Checkbox
          checked={value.noModifier}
          disabled={disabled}
          onChange={(e) => set({ noModifier: e.checked ?? false })}
        />
        Include rows with no modifier
      </label>
    </ModifierColumn>
  );
}

/** Normalize a specialty label for case/space-insensitive comparison (mirrors the server). */
function normalizeSpecialty(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ");
}

export function RateFileSplitter() {
  const { current: toastElement } = useContext(ToastContext);
  const fileUploadRef = useRef<FileUpload>(null);

  const [includeFacility, setIncludeFacility] = useState(true);
  const [includeNonFacility, setIncludeNonFacility] = useState(true);
  const [includeServiceLine, setIncludeServiceLine] = useState(true);

  // CLE-2514 — Specialty Overwrite. Specialties are pre-seeded with the four defaults but
  // fully editable; the override stays inert until at least one modifier is checked (so a
  // plain split of a radiology file is unaffected unless the analyst opts in).
  const [specialties, setSpecialties] = useState<string[]>(DEFAULT_SPECIALTIES);
  const [facilityMods, setFacilityMods] = useState<IModifierSelection>(
    EMPTY_MODIFIER_SELECTION
  );
  const [nonFacilityMods, setNonFacilityMods] = useState<IModifierSelection>(
    EMPTY_MODIFIER_SELECTION
  );

  const [lastStats, setLastStats] = useState<ISplitStats | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [splitFile, { isLoading }] = useSplitRateFileMutation();

  const submit = async (event: IFileUploadHandlerEvent) => {
    const file = event.files[0];
    if (!file) {
      // PrimeReact's FileUpload fires the upload handler even when nothing has been
      // chosen — without this guard, the backend cheerfully parses an empty payload
      // and returns "Detected as unknown / 0 rows" (Ani CLE-2323 follow-up).
      toastElement?.show({
        severity: "warn",
        summary: "No file selected",
        detail: "Choose a rate file first, then click Split.",
      });
      return;
    }
    if (!includeFacility && !includeNonFacility) {
      toastElement?.show({
        severity: "warn",
        summary: "Pick an output",
        detail: "Choose at least one of facility / non-facility.",
      });
      return;
    }

    setLastStats(null);
    setErrorMessage(null);

    try {
      const specialtyOverrides = buildSpecialtyOverrides(
        specialties,
        facilityMods,
        nonFacilityMods
      );
      const result = await splitFile({
        file,
        includeFacility,
        includeNonFacility,
        includeServiceLine,
        specialtyOverrides,
      }).unwrap();

      setLastStats(parseSplitStats(result.headers));

      const url = window.URL.createObjectURL(result.blob);
      const link = document.createElement("a");
      link.href = url;
      const inferredName = (file.name || "split").replace(/\.[^.]+$/, "");
      link.download = `${inferredName}-split.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toastElement?.show({
        severity: "success",
        summary: "Split complete",
        detail: "Your ZIP is downloading.",
      });
      fileUploadRef.current?.clear();
    } catch (e: any) {
      const detail =
        e?.data?.message ??
        e?.data?.error ??
        (typeof e?.data === "string" ? e.data : null) ??
        e?.message ??
        "Split failed.";
      setErrorMessage(detail);
    }
  };

  // CLE-2514 — whether the analyst has actually configured an override (specialties + at
  // least one modifier on a side). Drives the typo-detection banner below.
  const overrideActive =
    buildSpecialtyOverrides(specialties, facilityMods, nonFacilityMods).length >
    0;

  // Requested specialties the server reported as matching NO input row (likely typos),
  // compared case/space-insensitively against the echoed matched list.
  const matchedSpecialtyNorms = new Set(
    (lastStats?.specialtyMatchedServiceLines ?? []).map(normalizeSpecialty)
  );
  const unmatchedSpecialties =
    lastStats && overrideActive
      ? specialties
          .map((s) => s.trim())
          .filter((s) => s !== "")
          .filter((s) => !matchedSpecialtyNorms.has(normalizeSpecialty(s)))
      : [];

  return (
    <Wrapper>
      <h3>Split Physician Rate File</h3>
      <p style={{ margin: 0, color: "var(--gray-600)" }}>
        Upload one physician rate file (CSV or XLSX). The tool auto-detects the
        file shape, normalizes MOD/POS values, and returns a ZIP containing the
        facility rates and the non-facility (office) rates as separate CSVs.
      </p>
      <details style={{ color: "var(--gray-700)", fontSize: "0.9em" }}>
        <summary style={{ cursor: "pointer" }}>Expected columns</summary>
        <ul style={{ margin: "0.5rem 0 0 1.2rem", padding: 0 }}>
          <li>
            <strong>CPT code</strong> (required) — header can be any of:{" "}
            <code>CPT Code</code>, <code>CPT/HCPCS</code>,{" "}
            <code>Procedure Code</code>, <code>HCPCS</code>,{" "}
            <code>Proc_CD</code>, <code>CPT</code>
          </li>
          <li>
            <strong>Rate</strong> (required) — any of: <code>Rate</code>,{" "}
            <code>Fee Amount</code>, <code>Base Rate</code>,{" "}
            <code>Calculated rate</code>
          </li>
          <li>
            <strong>MOD / Modifier</strong> (optional)
          </li>
          <li>
            <strong>POS / Place of Service / Site Of Service</strong> (optional)
          </li>
          <li>
            <strong>Service Line / Type of Service</strong> (optional)
          </li>
          <li>
            <strong>Description / CPT Name</strong> (optional)
          </li>
        </ul>
        <p style={{ margin: "0.5rem 0 0 0" }}>
          Hyphens and underscores in header names are treated as spaces (so{" "}
          <code>Service-Line</code> and <code>Service_Line</code> both match{" "}
          <code>Service Line</code>).
        </p>
      </details>

      <Toggles>
        <label>
          <Checkbox
            checked={includeFacility}
            onChange={(e) => setIncludeFacility(e.checked ?? false)}
          />
          Include facility output
        </label>
        <label>
          <Checkbox
            checked={includeNonFacility}
            onChange={(e) => setIncludeNonFacility(e.checked ?? false)}
          />
          Include non-facility output
        </label>
        <label>
          <Checkbox
            checked={includeServiceLine}
            onChange={(e) => setIncludeServiceLine(e.checked ?? false)}
          />
          Include Service Line column
        </label>
      </Toggles>

      <OverridePanel>
        <div>
          <strong>Specialty Overwrite</strong>
          <div style={{ color: "var(--gray-600)", fontSize: "0.9em" }}>
            For these specialties (matched against the file&apos;s Service Line
            column), the output emits one row per selected modifier — tagged in
            a <code>Modifier</code> column — instead of a single rate. Leave
            every modifier unchecked to keep the normal split. Match is case-
            and spacing-insensitive.
          </div>
        </div>

        <div
          style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}
        >
          <label htmlFor="specialties" style={{ fontWeight: 600 }}>
            Specialties
          </label>
          <Chips
            inputId="specialties"
            value={specialties}
            onChange={(e) => setSpecialties(e.value ?? [])}
            disabled={isLoading}
            placeholder="Add a specialty and press Enter"
            separator=","
          />
        </div>

        <ModifierGroups>
          <ModifierGroup
            title="Facility Output Rules"
            value={facilityMods}
            onChange={setFacilityMods}
            disabled={isLoading}
          />
          <ModifierGroup
            title="Non-Facility (Office) Output Rules"
            value={nonFacilityMods}
            onChange={setNonFacilityMods}
            disabled={isLoading}
          />
        </ModifierGroups>
      </OverridePanel>

      <FileUpload
        ref={fileUploadRef}
        name="file"
        accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        customUpload
        uploadHandler={submit}
        maxFileSize={50 * 1024 * 1024}
        disabled={isLoading}
        chooseLabel="Choose file"
        uploadLabel="Split"
      />

      {isLoading && (
        <ProgressBar mode="indeterminate" style={{ height: "6px" }} />
      )}

      {errorMessage && (
        <Message
          severity="error"
          text={errorMessage}
          style={{ alignItems: "flex-start" }}
        />
      )}

      {lastStats && !errorMessage && lastStats.inputRows === 0 && (
        <Message
          severity="warn"
          content={
            <div>
              <strong>File parsed but no data rows were found.</strong>
              <div style={{ marginTop: "0.25rem" }}>
                Check that the file has the expected columns (see &quot;Expected
                columns&quot; above) and at least one row of data.
              </div>
            </div>
          }
        />
      )}

      {lastStats && !errorMessage && lastStats.inputRows > 0 && (
        <Message
          severity="success"
          content={
            <div>
              <div>
                <strong>{fileModeLabel(lastStats.fileMode)}</strong>
              </div>
              <div>
                {lastStats.inputRows.toLocaleString()} input rows →{" "}
                {lastStats.facilityRows.toLocaleString()} facility +{" "}
                {lastStats.nonFacilityRows.toLocaleString()} non-facility output
                rows ({lastStats.distinctCpts.toLocaleString()} distinct CPTs)
              </div>
              {(lastStats.droppedMissingCpt > 0 ||
                lastStats.droppedNoRate > 0) && (
                <div style={{ color: "var(--gray-700)", fontSize: "0.9em" }}>
                  Dropped: {lastStats.droppedMissingCpt} rows with no CPT,{" "}
                  {lastStats.droppedNoRate} rows with no rate
                </div>
              )}
              {(lastStats.overrideRows > 0 ||
                lastStats.specialtyMatchedServiceLines.length > 0) && (
                <div style={{ marginTop: "0.25rem" }}>
                  Specialty Overwrite emitted{" "}
                  {lastStats.overrideRows.toLocaleString()} row(s) across
                  specialty(ies):{" "}
                  {lastStats.specialtyMatchedServiceLines.join(", ")}
                </div>
              )}
              {unmatchedSpecialties.length > 0 && (
                <div style={{ color: "var(--orange-600)", fontSize: "0.9em" }}>
                  No rows matched these requested specialty(ies) — check for
                  typos: {unmatchedSpecialties.join(", ")}
                </div>
              )}
            </div>
          }
        />
      )}
    </Wrapper>
  );
}

export default RateFileSplitter;
